import {
  EMPTY_ERRORS,
  INVALID_SINGLE_VALUE_ERROR,
  MAX_MIN_ORDER_ERROR,
  MAX_VALUE_ERROR,
  MIN_MAX_ORDER_ERROR,
  MIN_VALUE_ERROR,
  RANGE_OPTIONS,
  TWO_DECIMAL_ERROR,
  TWO_DECIMAL_REGEX,
} from '../constants/VolPerfFilterRangeInputRadioGroup.constants';
import {FilterOption} from '../types/VolPerfFilter.types';
import {
  CurrentInputValidationArgs,
  NumberValidationConfig,
  RangeId,
  ValidationErrors,
  ValidationField,
  VolPerfFilterRangeDetails,
} from '../types/VolPerfRange.types';

export const buildCheckboxLabelText = (filterByText: string) =>
  filterByText === 'New Clients'
    ? 'Show only New Clients orders'
    : `Filter by ${filterByText}`;

export const getDefaultOptions = (options?: FilterOption[]) =>
  options?.map(option => ({
    ...option,
    selected: option.defaultVal || false,
  })) || [];

export const getRangeOptions = (selectedRange: RangeId) =>
  RANGE_OPTIONS.map(option => ({
    ...option,
    selected: option.id === selectedRange,
  }));

export const hasValidationErrors = (errors: ValidationErrors) =>
  Object.values(errors).some(Boolean);

export const isValidNumber = (
  input: string,
  {
    minDigit,
    maxDigit,
    isFloatAllowed,
    isNegativeNumAllowed,
  }: NumberValidationConfig,
) => {
  if (input === null || input === undefined) {
    return false;
  }

  const originalStr = String(input);
  if (originalStr !== originalStr.trim()) {
    return false;
  }

  const str = originalStr.trim();
  if (str === '' || str === '-') {
    return true;
  }

  const isNegative = str.startsWith('-');
  if (isNegative && !isNegativeNumAllowed) {
    return false;
  }

  const hasDecimal = str.includes('.');
  if (hasDecimal && !isFloatAllowed) {
    return false;
  }

  if (!/^[-]?\d*$/.test(str)) {
    return false;
  }

  if (str.includes('-') && str[0] !== '-') {
    return false;
  }

  const digitsOnly = str.replace(/[-]/g, '');
  if (digitsOnly.length === 0) {
    return false;
  }

  if (isNegative && minDigit < 0) {
    const minLength = Math.abs(minDigit);
    if (digitsOnly.length > minLength) {
      return false;
    }
  }

  if (!isNegative && maxDigit !== undefined && digitsOnly.length > maxDigit) {
    return false;
  }

  return true;
};

export const validateBetweenValues = (
  minVal: string,
  maxVal: string,
  nextErrors: ValidationErrors,
  numberConfig: NumberValidationConfig,
) => {
  if (minVal !== '') {
    if (!isValidNumber(minVal, numberConfig)) {
      nextErrors.minValue = MIN_VALUE_ERROR;
    } else if (!TWO_DECIMAL_REGEX.test(minVal)) {
      nextErrors.minValue = TWO_DECIMAL_ERROR;
    }
  }

  if (maxVal !== '') {
    if (!isValidNumber(maxVal, numberConfig)) {
      nextErrors.maxValue = MAX_VALUE_ERROR;
    } else if (!TWO_DECIMAL_REGEX.test(maxVal)) {
      nextErrors.maxValue = TWO_DECIMAL_ERROR;
    }
  }

  if (
    minVal !== '' &&
    minVal !== '-' &&
    maxVal !== '' &&
    maxVal !== '-' &&
    !nextErrors.minValue &&
    !nextErrors.maxValue
  ) {
    const minNum = Number(minVal);
    const maxNum = Number(maxVal);

    if (minNum >= maxNum) {
      nextErrors.minValue = MIN_MAX_ORDER_ERROR;
      nextErrors.maxValue = MAX_MIN_ORDER_ERROR;
    }
  }
};

export const validateCurrentInputs = ({
  isFilterEnabled,
  selectedRangeId,
  aboveValue,
  belowValue,
  ...numberConfig
}: CurrentInputValidationArgs): ValidationErrors => {
  if (!isFilterEnabled) {
    return EMPTY_ERRORS;
  }

  const nextErrors: ValidationErrors = {...EMPTY_ERRORS};

  if (selectedRangeId === 'above') {
    const value = aboveValue.trim();
    if (value === '' || value === '-' || !isValidNumber(value, numberConfig)) {
      nextErrors.rangeValue = INVALID_SINGLE_VALUE_ERROR;
    }
    return nextErrors;
  }

  if (selectedRangeId === 'below') {
    const value = belowValue.trim();
    if (value === '' || value === '-' || !isValidNumber(value, numberConfig)) {
      nextErrors.rangeValue = INVALID_SINGLE_VALUE_ERROR;
    }
    return nextErrors;
  }

  const minValue = aboveValue.trim();
  const maxValue = belowValue.trim();

  if (
    minValue === '' ||
    minValue === '-' ||
    !isValidNumber(minValue, numberConfig)
  ) {
    nextErrors.minValue = MIN_VALUE_ERROR;
  }

  if (
    maxValue === '' ||
    maxValue === '-' ||
    !isValidNumber(maxValue, numberConfig)
  ) {
    nextErrors.maxValue = MAX_VALUE_ERROR;
  }

  if (!nextErrors.minValue && !nextErrors.maxValue) {
    validateBetweenValues(minValue, maxValue, nextErrors, numberConfig);
  }

  return nextErrors;
};

export const filterInput = (input: string) => {
  let filtered = input.replace(/[^0-9-]/g, '');

  const minusIndex = filtered.indexOf('-');
  if (minusIndex !== -1) {
    if (minusIndex === 0) {
      filtered =
        filtered.substring(0, 1) + filtered.substring(1).replace(/-/g, '');
    } else {
      filtered = filtered.replace(/-/g, '');
    }
  }

  return filtered;
};

export const getErrorField = (
  rangeId: RangeId,
  fieldName: 'aboveValue' | 'belowValue',
): ValidationField =>
  rangeId === 'between'
    ? fieldName === 'aboveValue'
      ? 'minValue'
      : 'maxValue'
    : 'rangeValue';

export const normalizeDigit = (digit: number) =>
  digit < 0 ? Math.abs(digit) + 1 : digit;

export const buildResetState = (
  previousState: VolPerfFilterRangeDetails,
): VolPerfFilterRangeDetails => ({
  ...previousState,
  isFilterEnabled: false,
  resultRangeState: 'above',
  aboveValue: '',
  belowValue: '',
  isError: true,
  ValidationError: true,
  options: getDefaultOptions(previousState.options),
});

export const buildDisabledState = (
  previousState: VolPerfFilterRangeDetails,
): VolPerfFilterRangeDetails => ({
  ...previousState,
  isFilterEnabled: false,
  resultRangeState: 'above',
  aboveValue: '',
  belowValue: '',
  isError: true,
  ValidationError: false,
  options: getDefaultOptions(previousState.options),
});

export const buildEnabledState = (
  previousState: VolPerfFilterRangeDetails,
): VolPerfFilterRangeDetails => ({
  ...previousState,
  isFilterEnabled: true,
  resultRangeState: 'above',
  aboveValue: '',
  belowValue: '',
  isError: true,
  ValidationError: false,
});
